'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef, useState } from 'react';
import { capturePageView, schedulePostHogInitialization } from '@/lib/posthog';
import { getPostHogPageviewTrackingKey, WORKSPACE_URL_CHANGE_EVENT } from '@/lib/posthog-pageview';

interface PostHogProviderProps {
  children: React.ReactNode;
}

function PostHogPageTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [urlRevision, setUrlRevision] = useState(0);
  const lastTrackingKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const handleUrlChange = () => setUrlRevision(current => current + 1);
    window.addEventListener(WORKSPACE_URL_CHANGE_EVENT, handleUrlChange);
    return () => {
      window.removeEventListener(WORKSPACE_URL_CHANGE_EVENT, handleUrlChange);
    };
  }, []);

  useEffect(() => {
    const effectiveSearchParams =
      pathname === '/app/workspace' ? new URLSearchParams(window.location.search) : searchParams;
    const trackingKey = getPostHogPageviewTrackingKey(pathname, effectiveSearchParams);
    // Workspace query parameters also hold thread IDs and transient drawer state. Count only
    // meaningful view changes so those internal transitions do not become duplicate pageviews.
    if (lastTrackingKeyRef.current === trackingKey) return;
    lastTrackingKeyRef.current = trackingKey;
    const search = effectiveSearchParams?.toString() || '';
    const url = pathname + (search ? `?${search}` : '');

    capturePageView({
      $current_url: url,
      pathname,
      tracking_key: trackingKey,
    });
  }, [pathname, searchParams, urlRevision]);

  return null;
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  useEffect(() => {
    schedulePostHogInitialization();
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <PostHogPageTracker />
      </Suspense>
      {children}
    </>
  );
}
